import React, { useState } from 'react';
import { ChevronLeftIcon, MonitorIcon, SmartphoneIcon, PaletteIcon, MessageSquareIcon, SlidersHorizontalIcon, CodeIcon, Undo2Icon, Redo2Icon, CheckIcon, EyeIcon, PencilIcon } from 'lucide-react';
import { WidgetPreview } from './WidgetPreview';
import { WidgetContent, WidgetBehaviour, WidgetInstall } from './panels/WidgetSetup';
import { WidgetAppearance } from './panels/WidgetAppearance';
import { WidgetConfig } from '../../data/widgetConfig';
import { ShopConfig } from '../../data/shopConfig';
import { storefrontFeatured } from '../../utils/storefront';
type WidgetTab = 'appearance' | 'content' | 'behaviour' | 'install';
const TABS: {
  id: WidgetTab;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'appearance',
  label: 'Design',
  icon: PaletteIcon
}, {
  id: 'content',
  label: 'Content',
  icon: MessageSquareIcon
}, {
  id: 'behaviour',
  label: 'Behaviour',
  icon: SlidersHorizontalIcon
}, {
  id: 'install',
  label: 'Install',
  icon: CodeIcon
}];
interface WidgetStudioProps {
  widget: WidgetConfig;
  updateWidget: (patch: Partial<WidgetConfig>) => void;
  config: ShopConfig;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClose: () => void;
  onSave: () => void;
  onUpgrade: () => void;
  canRemoveBranding: boolean;
}

/**
 * Widget Studio — the merchant's widget rendered over a stand-in for their own
 * website, with the controls beside it. Same shape as the Shop Studio so the
 * two channels feel like one tool.
 */
export function WidgetStudio({
  widget,
  updateWidget,
  config,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onClose,
  onSave,
  onUpgrade,
  canRemoveBranding
}: WidgetStudioProps) {
  const [tab, setTab] = useState<WidgetTab>('appearance');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [state, setState] = useState<'open' | 'closed'>('open');
  const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit');
  const products = storefrontFeatured(config).slice(0, 2).map(v => ({
    id: v.id,
    name: v.name,
    image: v.image,
    price: v.monthly
  }));
  const panelProps = {
    widget,
    update: updateWidget,
    brand: {
      primary: config.theme.primary,
      accent: config.theme.accent
    },
    handle: config.handle,
    canRemoveBranding,
    onUpgrade,
    previewLauncher: () => {
      setState('closed');
      setMobileView('preview');
    }
  };
  const panel = tab === 'appearance' ? <WidgetAppearance {...panelProps} data-id="element-5187" data-mp="2lmvz5fbszrmi" /> : tab === 'content' ? <WidgetContent {...panelProps} data-id="element-5188" data-mp="2qn0e30x2ceam" /> : tab === 'behaviour' ? <WidgetBehaviour {...panelProps} data-id="element-5189" data-mp="2vg39lelehxtg" /> : <WidgetInstall {...panelProps} data-id="element-5190" data-mp="2vgux024ugp8n" />;
  return <div className="fixed inset-0 z-[60] bg-slate-100 flex flex-col" data-id="element-5191" data-mp="1xyuskby6ql9q">
      <header className="flex-shrink-0 flex items-center gap-3 px-3 lg:px-5 h-14 bg-white border-b border-slate-200" data-id="element-5192" data-mp="22y7dgr5hxi9o">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-5193" data-mp="k2l288fgjkdk">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-5194" data-mp="b4r5wxhp0qqe" />
          <span className="hidden sm:inline" data-id="element-5195" data-mp="62s780wwehzs">Studio</span>
        </button>

        <div className="w-px h-6 bg-slate-200 hidden sm:block" data-id="element-5196" data-mp="15a6gxt1b92v" />

        <div className="min-w-0 hidden sm:block" data-id="element-5197" data-mp="16l8ot6u08rv">
          <p className="text-sm font-bold text-dark-navy truncate leading-tight" data-id="element-5198" data-mp="3sid60sos3w0n">
            Website Widget
          </p>
          <p className="text-[11px] text-slate-400 truncate leading-tight" data-id="element-5199" data-mp="3oicdy58rbmb4">
            {widget.domains[0] ?? 'your website'}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-1.5 lg:gap-2" data-id="element-5200" data-mp="3ek6gq0xwb4cl">
          <div className="hidden lg:inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-5201" data-mp="3elhki0rccc3p">
            {[{
            id: 'desktop' as const,
            label: 'Desktop',
            icon: MonitorIcon
          }, {
            id: 'mobile' as const,
            label: 'Mobile',
            icon: SmartphoneIcon
          }].map(d => <button key={d.id} onClick={() => setDevice(d.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${device === d.id ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5202" data-mp="18qp8n463df1p">
                <d.icon className="w-3.5 h-3.5" data-id="element-5203" data-mp="kub4bz4hcfea" />
                {d.label}
              </button>)}
          </div>

          <div className="inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-5204" data-mp="pto0jah8qytj">
            {[{
            id: 'open' as const,
            label: 'Open'
          }, {
            id: 'closed' as const,
            label: 'Closed'
          }].map(s => <button key={s.id} onClick={() => setState(s.id)} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${state === s.id ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5205" data-mp="3jpc23s5l0vc">
                {s.label}
              </button>)}
          </div>

          <div className="hidden sm:flex items-center gap-1" data-id="element-5206" data-mp="n7om1cew83zp">
            <button onClick={onUndo} disabled={!canUndo} aria-label="Undo" className="p-2 rounded-lg text-slate-500 hover:text-primary-indigo hover:bg-slate-50 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-5207" data-mp="n9hm28v03pyq">
              <Undo2Icon className="w-4 h-4" data-id="element-5208" data-mp="32yt09owfn43f" />
            </button>
            <button onClick={onRedo} disabled={!canRedo} aria-label="Redo" className="p-2 rounded-lg text-slate-500 hover:text-primary-indigo hover:bg-slate-50 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-5209" data-mp="2y0hsrktm15od">
              <Redo2Icon className="w-4 h-4" data-id="element-5210" data-mp="1awqaewqppbyj" />
            </button>
          </div>

          <button onClick={() => setMobileView(v => v === 'edit' ? 'preview' : 'edit')} className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 text-xs font-semibold text-slate-600" data-id="element-5211" data-mp="1fwm525dirr1f">
            {mobileView === 'edit' ? <>
                <EyeIcon className="w-3.5 h-3.5" data-id="element-5212" data-mp="1ulio0xh46ogt" />
                Preview
              </> : <>
                <PencilIcon className="w-3.5 h-3.5" data-id="element-5213" data-mp="1yoez834briaq" />
                Edit
              </>}
          </button>

          <button onClick={onSave} className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-5214" data-mp="28ki95v5f4l88">
            <CheckIcon className="w-4 h-4" data-id="element-5215" data-mp="2do0zfqvk660i" />
            <span className="hidden sm:inline" data-id="element-5216" data-mp="28q95krbcs9to">Save</span>
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex" data-id="element-5217" data-mp="24p75675p9rq4">
        <main className={`flex-1 min-w-0 p-3 lg:p-5 ${mobileView === 'preview' ? 'block' : 'hidden lg:block'}`} data-id="element-5218" data-mp="24qi7s3nestfn">
          <div className="w-full h-full rounded-2xl bg-white border border-slate-200 shadow-[0_12px_40px_rgba(11,18,32,0.08)] overflow-hidden flex flex-col" data-id="element-5219" data-mp="31h0y9et7mgc9">
            <div className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50" data-id="element-5220" data-mp="2ro283si2ehqj">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-5221" data-mp="2nnjh20nzxfpe" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-5222" data-mp="3k6hct61qc9bo" />
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" data-id="element-5223" data-mp="3feftj0659ic5" />
              <span className="mx-auto text-[11px] text-slate-400 truncate" data-id="element-5224" data-mp="3fd4o6vx1w2ix">
                {widget.domains[0] ?? 'your-website.com'}
              </span>
            </div>
            <div className="flex-1 min-h-0 flex items-stretch justify-center bg-slate-100 p-4" data-id="element-5225" data-mp="7pn3bn5vhlme">
              <div className="relative bg-white overflow-hidden rounded-xl border border-slate-200 mx-auto" style={device === 'mobile' ? {
              width: 390,
              maxWidth: '100%'
            } : {
              width: '100%'
            }} data-id="element-5226" data-mp="3p6d9kl9zhwpy">
                <WidgetPreview widget={widget} siteName={config.publicName} products={products} state={state} device={device} data-id="element-5227" data-mp="gp0f4mhgymaa" />
              </div>
            </div>
          </div>
        </main>

        <aside className={`w-full lg:w-[430px] xl:w-[470px] flex-shrink-0 bg-white lg:border-l border-slate-200 flex flex-col min-h-0 ${mobileView === 'edit' ? 'flex' : 'hidden lg:flex'}`} data-id="element-5228" data-mp="1jhmddvyj5gar">
          <div className="flex-shrink-0 p-3 border-b border-slate-100" data-id="element-5229" data-mp="1si1111yyv7hf">
            <div className="flex p-1 bg-slate-100 rounded-xl" data-id="element-5230" data-mp="1sg6ooc1rgnoy">
              {TABS.map(t => {
              const Icon = t.icon;
              const isActive = t.id === tab;
              return <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 inline-flex items-center justify-center gap-1.5 px-1.5 py-2 rounded-lg text-[12px] font-semibold transition-colors duration-150 ease-out ${isActive ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5231" data-mp="10oqyy94wqkui">
                    <Icon className="w-4 h-4" data-id="element-5232" data-mp="h0rnew2ir9o1" />
                    {t.label}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-5" data-id="element-5233" data-mp="38aq2mu9slsy0">
            {panel}
          </div>
        </aside>
      </div>
    </div>;
}